"use client"

import { useEffect } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { CURRENCY_CHANGE_EVENT, CURRENCY_STORAGE_KEY } from "@/lib/currency-display"
import { trackYohoEvent } from "@/lib/analytics/track"

function getNavArea(element: Element) {
  if (element.closest("header")) return "header"
  if (element.closest("footer")) return "footer"
  if (element.closest("#mobile-menu")) return "mobile_menu"
  if (element.closest("nav")) return "nav"
  return "content"
}

function getNavLabel(anchor: HTMLAnchorElement) {
  const text = anchor.textContent?.trim()
  if (text) return text.slice(0, 80)

  const ariaLabel = anchor.getAttribute("aria-label")
  if (ariaLabel) return ariaLabel

  throw new Error("[analytics] nav link is missing label")
}

function getSafeLinkUrl(anchor: HTMLAnchorElement) {
  const href = anchor.getAttribute("href")
  if (!href) throw new Error("[analytics] nav link is missing href")

  const url = new URL(href, window.location.origin)
  if (url.origin === window.location.origin) {
    return url.pathname
  }
  return url.origin + url.pathname
}

export function AnalyticsRouteListener() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    const trackRouteView = () => {
      if (!window.localStorage.getItem(CURRENCY_STORAGE_KEY)) return
      trackYohoEvent("route_view", {
        route_path: pathname,
      })
    }

    trackRouteView()
    window.addEventListener(CURRENCY_CHANGE_EVENT, trackRouteView, { once: true })
    return () => window.removeEventListener(CURRENCY_CHANGE_EVENT, trackRouteView)
  }, [pathname, searchParams])

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return

      const anchor = target.closest("a[href]")
      if (!(anchor instanceof HTMLAnchorElement)) return
      if (!anchor.closest("header, footer, nav, #mobile-menu")) return

      trackYohoEvent("nav_click", {
        nav_area: getNavArea(anchor),
        nav_label: getNavLabel(anchor),
        link_url: getSafeLinkUrl(anchor),
      })
    }

    document.addEventListener("click", handleClick, { capture: true })
    return () => document.removeEventListener("click", handleClick, { capture: true })
  }, [])

  return null
}
